import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { DateRangePicker, RangeKeyDict } from 'react-date-range';
import { useClickAway } from 'hooks/useClickAway';
import { useTranslation } from 'react-i18next';
import { faMoon } from '@fortawesome/free-regular-svg-icons';
import MovablePlaceholder from '../MovablePlaceholder/MovablePlaceholder';
import { addDays, getDaysDifference } from 'utils/utils';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { dateFormat, getLocaleSet } from 'utils/ui';
import Button from '../Button/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import withErrorBoundary from 'components/ui/withErrorBoundaries';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import SelectCustom from '../SelectCustom/SelectCustom';
export type DrpValue = {
  startDate: Date;
  endDate: Date;
  color: string;
  disabled?: boolean;
};

export type DrpValueStr = {
  startDate: string;
  endDate: string;
};

interface DaterangePickerProps {
  onChange: (value: DrpValueStr) => void;
  value: DrpValueStr;
  appendToRight?: boolean;
  movablePlaceholder?: string;
  movablePlaceholderIcon?: any;
  additionalValue?: DrpValue;
  maxDateRange?: number;

  showNights?: boolean;
  vertical?: boolean;
  scrollOnFocusMobile?: boolean;
}

const DaterangePickerCustom: FC<DaterangePickerProps> = (props) => {
  const colorContrast = getComputedStyle(document.body).getPropertyValue('--color-contrast');
  const [t] = useTranslation();
  const [value, setValue] = useState<DrpValue>({
    startDate: new Date(),
    endDate: new Date(),
    color: colorContrast,
  });
  const [open, setOpen] = useState(false);
  const format = "EEE '<span class=''text-bold text-uppercase''>'dd MMM'</span>,' yy";
  const [defaultDate] = useState<DrpValueStr>(props.value);
  const inputRef = useRef<any>();
  const initScrollPosition = useRef(0);

  useEffect(() => {
    const { startDate, endDate } = props.value;
    setValue({
      startDate: new Date(startDate),
      endDate: new Date(endDate),
      color: colorContrast,
    });
  }, [props.value]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDatepicker = () => {
    setOpen(true);
    setTimeout(() => {
      const days = document.querySelectorAll('.rdrWeekDay');

      days.forEach((day) => {
        day.innerHTML = day.innerHTML.replace('יום', '');
      });

      if (window.innerWidth < 768 && props.scrollOnFocusMobile && inputRef.current) {
        initScrollPosition.current = window.scrollY;
        const offset = 40;
        const elementPosition = inputRef.current.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
      }
    });
  };

  const close = () => {
    setOpen(false);

    if (window.innerWidth < 768 && props.scrollOnFocusMobile) {
      window.scrollTo({ top: initScrollPosition.current, behavior: 'smooth' });
      initScrollPosition.current = 0;
    }
  };
  const ref = useClickAway(close);

  const onChange = (value: RangeKeyDict) => {
    if (value['range1'].endDate && value['range1'].startDate) {
      const maxRangeDays = props.maxDateRange || 30;
      const day = 24 * 60 * 60 * 1000;
      const daysDiff =
        (value['range1'].endDate.getTime() - value['range1'].startDate.getTime()) / day;

      if (daysDiff < maxRangeDays || maxRangeDays === -1) {
        props.onChange({
          startDate: dateFormat(value['range1'].startDate, 'yyyy-MM-dd', 'en'),
          endDate: dateFormat(value['range1'].endDate, 'yyyy-MM-dd', 'en'),
        });
      } else {
        props.onChange({
          startDate: dateFormat(value['range1'].startDate, 'yyyy-MM-dd', 'en'),
          endDate: dateFormat(addDays(maxRangeDays, value['range1'].startDate), 'yyyy-MM-dd', 'en'),
        });
      }
    }
  };



  const nights = getDaysDifference(value.startDate.toString(), value.endDate.toString());
  const nightsDropdownValue = Math.min(30, Math.max(0, nights));
  const nightsOptions = Array.from({ length: 31 }, (_, index) => ({
    label: index.toString(),
    value: index,
  })).slice(1);

  return (
    <div className="position-relative">
      <div
        className={
          'form-control form-group-calendar' +
          (props.movablePlaceholder ? ' movable-placeholder-input' : '') +
          (props.vertical ? ' form-group-calendar-vertical' : '')
        }
        onClick={() => openDatepicker()}
        ref={inputRef}
      >
        {!props.vertical && (
          <Fragment>
            <span dangerouslySetInnerHTML={{ __html: dateFormat(value.startDate, format) }}></span>
            <span className="ph-5">-</span>
            <span dangerouslySetInnerHTML={{ __html: dateFormat(value.endDate, format) }}></span>
          </Fragment>
        )}
        {props.vertical && (
          <Fragment>
            <div className="form-group-calendar-vertical-big">
              {dateFormat(value.startDate, 'dd')}
            </div>
            <div className="form-group-calendar-vertical-dates">
              <div className="text-bold">
                {dateFormat(value.startDate, 'MMM')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
              <div>
                {dateFormat(value.startDate, 'eee')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
            </div>
            <div className="form-group-calendar-vertical-div"></div>

            <div className="form-group-calendar-vertical-big">
              {dateFormat(value.endDate, 'dd')
                .split('')
                .map((m, i) => (
                  <span key={i}>{m}</span>
                ))}
            </div>
            <div className="form-group-calendar-vertical-dates">
              <div className="text-bold">
                {dateFormat(value.endDate, 'MMM')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
              <div>
                {dateFormat(value.endDate, 'eee')
                  .split('')
                  .map((m, i) => (
                    <span key={i}>{m}</span>
                  ))}
              </div>
            </div>
          </Fragment>
        )}
        {props.showNights && (
          <span className="ph-10 form-group-nights">
            <span className="text-bold">
              {getDaysDifference(value.startDate.toString(), value.endDate.toString())}{' '}
            </span>
            <FontAwesomeIcon icon={faMoon} />
          </span>
        )}
      </div>
      {props.movablePlaceholder && (
        <MovablePlaceholder
          label={props.movablePlaceholder}
          showSmall={open || !!value.startDate}
          icon={props.movablePlaceholderIcon}
        />
      )}
      {open && (
        <div
          ref={ref}
          className={
            'daterangepicker-wr' + (props.appendToRight ? ' daterangepicker-wr-right' : '')
          }
        >
          <DateRangePicker
            ranges={props.additionalValue ? [value, props.additionalValue] : [value]}
            maxDate={
              props.additionalValue ? addDays(7, props.additionalValue.startDate) : addDays(800)
            }
            minDate={
              props.additionalValue ? addDays(-7, props.additionalValue.startDate) : new Date()
            }
            onChange={onChange}
            months={2}
            direction="horizontal"
            locale={getLocaleSet()}
            preventSnapRefocus={true}
          />{' '}
          <div className="daterangepicker-bottom">
            <div className="flex-row mb-15" style={{ alignItems: 'center' }}>
              <div className="flex-column">
                <div className="text-light mb-5">{t('search.check_in')}</div>
                <div className="text-bold">{dateFormat(value.startDate, 'EEE - dd MMM, yyyy')}</div>
              </div>
              <span className="ph-20">
                <FontAwesomeIcon icon={faArrowRight} />
              </span>
              <div className="flex-column">
                <div className="text-light mb-5">{t('search.check_out')}</div>
                <div className="text-bold">{dateFormat(value.endDate, 'EEE - dd MMM, yyyy')}</div>
              </div>
              {props.showNights && (
                <div className="flex-column ph-20 dropdown-bold" style={{ width: 100 }}>
                  <div className="text-light mb-5">{t('hotel.nights_up')}</div>
                  <SelectCustom
                    options={nightsOptions}
                    value={nightsDropdownValue}
                    isDropdown
                    menuPlacement='top'
                    onChange={(nextNights) =>
                      onChange({
                        range1: {
                          startDate: value.startDate,
                          endDate: addDays(nextNights, value.startDate),
                        },
                      })
                    }
                  />
                </div>
              )}
            </div>

            <div className="flex-row">
              <Button className="btn btn-primary mr-a" title={t('choose')} onClick={close} />
              <Button
                title={t('reset')}
                className="btn btn-link"
                onClick={() =>
                  onChange({
                    range1: {
                      startDate: new Date(defaultDate.startDate),
                      endDate: new Date(defaultDate.endDate),
                    },
                  })
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(DaterangePickerCustom, 'DaterangePickerCustom');
